import { buildWsUrl, WS_EVENTS, type WsEnvelope, type WsEventName } from "./constants";

/** Intervalle d'envoi des pings applicatifs (ms). */
const PING_INTERVAL_MS = 25000;
/** Sans aucun message reçu pendant ce délai, la connexion est jugée morte. */
const PONG_TIMEOUT_MS = 70000;
/** Plafond du backoff exponentiel de reconnexion (ms). */
const RECONNECT_MAX_DELAY_MS = 30000;

export type WsEventHandler = (payload: Record<string, unknown>) => void;

type ListenerMap = Map<string, Set<WsEventHandler>>;

export type WebSocketClientOptions = {
  token: string;
  buildingId?: number | null;
  client?: "accueilv2" | "fronthagd";
  onConnectionChange?: (connected: boolean) => void;
  onError?: (error: string) => void;
};

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private listeners: ListenerMap = new Map();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private shouldReconnect = false;
  private options: WebSocketClientOptions | null = null;
  private lastActivityAt = 0;
  private netListenersAttached = false;

  on(event: WsEventName | string, handler: WsEventHandler): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
    return () => this.off(event, handler);
  }

  off(event: WsEventName | string, handler: WsEventHandler): void {
    this.listeners.get(event)?.delete(handler);
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  connect(options: WebSocketClientOptions): void {
    const sameToken = this.options?.token === options.token;
    this.options = options;
    this.shouldReconnect = true;
    this.reconnectAttempts = 0;
    this.attachNetworkListeners();

    if (this.ws?.readyState === WebSocket.OPEN && sameToken) {
      this.initConnection(options.buildingId);
      this.options?.onConnectionChange?.(true);
      return;
    }

    this.closeSocket(1000, "reconnect");
    this.openSocket();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.clearTimers();
    this.closeSocket(1000, "client_disconnect");
    this.options?.onConnectionChange?.(false);
  }

  send(event: WsEventName | string, payload: Record<string, unknown> = {}): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false;
    }
    const envelope: WsEnvelope = { event, payload };
    this.ws.send(JSON.stringify(envelope));
    return true;
  }

  initConnection(buildingId?: number | null): void {
    const resolvedBuildingId = buildingId ?? this.options?.buildingId ?? undefined;
    this.send(WS_EVENTS.CONNECTION_INIT, {
      ...(resolvedBuildingId != null ? { buildingId: resolvedBuildingId } : {}),
      client: this.options?.client ?? "accueilv2",
    });
  }

  private openSocket(): void {
    if (!this.options?.token) return;

    this.clearTimers();
    const url = buildWsUrl(this.options.token);
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.lastActivityAt = Date.now();
      this.options?.onConnectionChange?.(true);
      this.initConnection(this.options?.buildingId);
      this.pingTimer = setInterval(() => {
        // Watchdog : connexion à moitié morte (aucun message ni pong reçu)
        // → fermeture forcée pour déclencher la reconnexion automatique.
        if (Date.now() - this.lastActivityAt > PONG_TIMEOUT_MS) {
          this.closeSocket(4000, "pong_timeout");
          return;
        }
        this.send(WS_EVENTS.PING, {});
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      this.lastActivityAt = Date.now();
      try {
        const data = JSON.parse(String(event.data)) as WsEnvelope;
        if (!data?.event) return;
        this.dispatch(data.event, data.payload ?? {});
      } catch {
        this.options?.onError?.("Message WebSocket invalide");
      }
    };

    ws.onerror = () => {
      this.options?.onError?.("Erreur WebSocket");
    };

    ws.onclose = (event) => {
      this.options?.onConnectionChange?.(false);
      this.clearPing();
      this.ws = null;
      if (event.code === 4001) {
        this.shouldReconnect = false;
        this.options?.onError?.("Authentification WebSocket refusée (token invalide)");
        return;
      }
      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    };
  }

  private dispatch(event: string, payload: Record<string, unknown>): void {
    const handlers = this.listeners.get(event);
    handlers?.forEach((handler) => handler(payload));

    if (event === WS_EVENTS.ERROR) {
      const message =
        typeof payload.message === "string" ? payload.message : "Erreur WebSocket";
      this.options?.onError?.(message);
    }
  }

  /**
   * Backoff exponentiel plafonné, sans abandonner : les tentatives continuent
   * tant que shouldReconnect est vrai (seul un 4001 ou disconnect() arrête).
   */
  private scheduleReconnect(): void {
    const delay = Math.min(
      1000 * 2 ** Math.min(this.reconnectAttempts, 5),
      RECONNECT_MAX_DELAY_MS,
    );
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => this.openSocket(), delay);
  }

  /** Reconnexion immédiate si absente (retour réseau, onglet visible). */
  private ensureConnected(): void {
    if (!this.shouldReconnect || !this.options?.token) return;
    const state = this.ws?.readyState;
    if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
    this.clearTimers();
    this.reconnectAttempts = 0;
    this.openSocket();
  }

  /** Attache une seule fois les listeners réseau/visibilité du navigateur. */
  private attachNetworkListeners(): void {
    if (this.netListenersAttached || typeof window === "undefined") return;
    this.netListenersAttached = true;
    window.addEventListener("online", () => this.ensureConnected());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") this.ensureConnected();
    });
  }

  private closeSocket(code: number, reason: string): void {
    if (!this.ws) return;
    const socket = this.ws;
    this.ws = null;
    socket.close(code, reason);
  }

  private clearPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private clearTimers(): void {
    this.clearPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

/** Instance singleton partagée dans toute l'app. */
export const wsClient = new WebSocketClient();
